import React, { useEffect, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import { useNavigate, useParams } from "react-router-dom";
import InfinitePaginatedList from "src/components/common/Pagination/InfinitePaginatedList";
import Loader from "src/components/common/Loader";
import Layout from "src/components/app/layout/Layout";
import Header from "src/components/app/layout/Header";
import {
  getPSPSymptomsRequest,
  getSymptomsRequest,
} from "src/redux/slices/symptomsSlice";
import FilterTabs from "src/components/common/Tabs/FilterTabs";
import moment from "moment";
import {
  PspSymptom,
  SymptomsOnSingleDay,
  SymptomsOnTimeInterval,
} from "src/types/symptoms.types";
import { Stethoscope } from "lucide-react";
import {
  DATE_FORMAT_STRING,
  TIME_FORMAT_STRING,
} from "src/constants/dataTimeFormats";

const SymptomLogsScreen: React.FC = () => {
  const dispatch = useAppDispatch();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const params = useParams();
  const supportProgramKey = params["support-program-key"];
  const navigate = useNavigate();
  const patientId = useAppSelector(
    (state) => state.auth.patient.data?.patient_id
  );
  const supportProgramType = useAppSelector(
    (state) => state.auth.patient.data?.patient_meta_info?.support_program_type
  );
  const pspSymptoms = useAppSelector(
    (state) => state.symptoms.pspSymptoms.data
  );
  const [activeSymptomIds, setActiveSymptomIds] = useState<string>("");

  useEffect(() => {
    fetchPSPSymptoms();
  }, []);

  const fetchPSPSymptoms = () => {
    return new Promise((resolve, reject) => {
      dispatch(
        getPSPSymptomsRequest({
          resolve,
          reject,
          payload: {
            patient_id: patientId || "c6fbab03-4a8a-11f0-ab23-0022486e34ba",
          },
          query_params: {
            support_program_type: "tukavo", //supportProgramType || "onco",
          },
        })
      );
    });
  };

  const handleSymptomTabClick = (symptomId: string) => {
    setActiveSymptomIds(symptomId);
    if (activeSymptomIds.includes(symptomId)) {
      // Remove topic ID
      setActiveSymptomIds("");
    } else {
      // Add topic ID
      setActiveSymptomIds(symptomId);
    }
  };

  const fetchSymptoms = (
    page: number
  ): Promise<{ items: SymptomsOnSingleDay[]; hasMore: boolean }> => {
    return new Promise((resolve, reject) => {
      dispatch(
        getSymptomsRequest({
          resolve,
          reject,
          payload: {
            page: page,
            patient_id: patientId || "c6fbab03-4a8a-11f0-ab23-0022486e34ba", //"e2d89a1d-91c7-11ef-9117-0022486e34ba", //"3bff0ba2-e1d1-11ee-ae7b-0022486e34ba",
            ...(activeSymptomIds &&
              activeSymptomIds !== "" && { symptom_name: activeSymptomIds }),
            // "symptoms_id": "0aa32e7c-f0b4-47f0-9dcf-37068857fb5a",
            // "visit_id" : "04f3cb92-3182-4613-8e5a-fd1554ab6859",
            // motivational_quotes : true
          },
        })
      );
    }).then((res: any) => {
      const items = (res?.data?.symptoms || []) as SymptomsOnSingleDay[];
      const hasMore = items.length === 10;
      return { items, hasMore };
    });
  };

  const renderDaySymtoms = (symptoms: SymptomsOnTimeInterval[]) =>
    symptoms?.map((symptom: SymptomsOnTimeInterval, index: number) => (
      <LogHistoryItem
        key={index}
        icon={symptom.symptoms_info?.symptom_image_url}
        title={
          symptom.symptoms_info?.data?.your_notes || symptom.symptoms_info.name
        }
        timestamp={symptom.symptom_updated}
      />
    ));

  return (
    <Layout
      supportProgramKey={supportProgramKey || ""}
      showBottomBar={true}
      enableTheme={true}
      header={
        <Header
          title="Symptom Logs"
          showBackButton
          onBackClick={() => navigate(-1)}
        />
      }
    >
      <div className="flex flex-col h-full">
        {/* Fixed Header */}
        <div className="flex-shrink-0">
          <div className="p-4 bg-white border-b border-gray-200">
            <FilterTabs
              tabs={
                pspSymptoms?.map((symptom: PspSymptom, index: number) => ({
                  id: symptom.name,
                  label: symptom.name,
                })) || []
              }
              activeTabIds={[activeSymptomIds]}
              onTabClick={handleSymptomTabClick}
            />
          </div>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-hidden">
          <div
            id="appointment-scroll-container"
            ref={scrollContainerRef}
            className="h-full overflow-y-auto p-4"
          >
            <InfinitePaginatedList
              fetchData={fetchSymptoms}
              containerHeight="100%"
              scrollableTarget="appointment-scroll-container"
              renderItem={(symptoms: SymptomsOnSingleDay, index: number) => (
                <div key={index} className="space-y-4 mb-4">
                  {renderDaySymtoms(symptoms?.["2"])}
                  {renderDaySymtoms(symptoms?.["1"])}
                  {renderDaySymtoms(symptoms?.["0"])}
                </div>
              )}
              emptyMessage={
                <div className="flex justify-center items-center h-64">
                  <p className="text-gray-500">No symptoms found</p>
                </div>
              }
              loader={
                <div className="flex justify-center items-center py-4">
                  <Loader />
                </div>
              }
            />
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default SymptomLogsScreen;

const LogHistoryItem = ({
  icon,
  title,
  timestamp,
}: {
  icon: string | undefined;
  title: string;
  timestamp: string;
}) => {
  const Icon = icon ? (
    <img src={icon} alt="symptom" className="w-5 h-5 object-contain" />
  ) : (
    <Stethoscope className="w-5 h-5 text-[var(--app-primary)]" />
  );
  const date = moment(timestamp).format(DATE_FORMAT_STRING);
  const time = moment.utc(timestamp).local().format(TIME_FORMAT_STRING);

  return (
    <div className="w-full p-4 shadow-sm border-0 bg-white rounded-lg overflow-hidden">
      <div className="flex items-center gap-4">
        <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-[var(--icon-bg-primary)]">
          <span className="text-md text-[var(--app-primary)]">{Icon}</span>
        </div>
        <div className="w-full flex flex-col gap-1">
          <div className="w-full flex items-start justify-between gap-2">
            <span className="flex-1 text-sm font-medium block line-clamp-1">
              {title}
            </span>
          </div>
          <div className="text-xs font-light text-gray-500 w-full">
            {date} • {time}
          </div>
        </div>
      </div>
    </div>
  );
};
